import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Category,
    CategoryNodeSummary,
    CategoryNodeType,
    MetadataCategorySearchDTO,
} from "../../types/Category";
import {
    autocompleteCategories,
    searchCategories,
    getRoots,
    getChildren,
    getCategoryNodeById,
    getSubtree,
    createBranch,
    moveCategory,
    deleteCategoryNode,
    patchCategoryUpdateName,
    patchCategoryUpdateDescription,
} from "../../api/CategoryAPI";
import { countInventoriesByCategories } from "../../api/InventoryAPI";
import { logApiError, getUserMessage } from "../../api/apiError";

interface Props {
    token: string;

    /** Enables tree-authoring controls (create branch, move, delete, rename). */
    authoring?: boolean;

    /** Called with the full leaf category (parameters included) when a leaf is reached. */
    onSelectLeaf: (leaf: Category) => void;

    /** Authoring: start creating a leaf category under the given parent (null = root). */
    onCreateLeafHere?: (parentId: string | null, initialName: string) => void;

    /** Authoring: called after a branch is created / moved / deleted / renamed. */
    onTreeChanged?: () => void;

    /** Navigation: offer a "create new" action when the typed name has no match. */
    onCreateNew?: (initialName: string) => void;
    createNewLabel?: string;
    requestSavedFor?: string | null;

    /** Highlight the currently-selected leaf (id) in the tree. */
    selectedLeafId?: string | null;

    /** Bump to force a reload of the loaded tree (after an external mutation). */
    refreshSignal?: number;
}

const ROOT = "__ROOT__";

function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
}

const isLeaf = (n: CategoryNodeSummary | Category) => n.nodeType === CategoryNodeType.LEAF;

const toSummary = (leaf: Category): CategoryNodeSummary => ({
    id: leaf.id,
    version: leaf.version,
    name: leaf.name,
    description: leaf.description,
    parentId: leaf.parentId,
    ancestorIds: leaf.ancestorIds ?? [],
    depth: leaf.depth ?? 0,
    globallyUnique: leaf.globallyUnique ?? false,
    nodeType: CategoryNodeType.LEAF,
});

const CategoryTreeNavigator: React.FC<Props> = ({
    token,
    authoring = false,
    onSelectLeaf,
    onCreateLeafHere,
    onTreeChanged,
    onCreateNew,
    createNewLabel = "Create Category",
    requestSavedFor,
    selectedLeafId,
    refreshSignal,
}) => {
    // --- search box ---
    const [query, setQuery] = useState("");
    const [hits, setHits] = useState<MetadataCategorySearchDTO[]>([]);
    const [searching, setSearching] = useState(false);
    // After picking a hit we set the query to its name; skip the search that
    // change would otherwise trigger so the dropdown doesn't reopen.
    const skipNextSearchRef = useRef(false);

    // --- tree state ---
    // childrenByParent[ROOT] = root nodes; childrenByParent[<id>] = that node's children.
    const [childrenByParent, setChildrenByParent] = useState<Record<string, CategoryNodeSummary[]>>({});
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [activeNode, setActiveNode] = useState<CategoryNodeSummary | null>(null);
    const [loadingRoots, setLoadingRoots] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // --- authoring local state ---
    const [newBranchName, setNewBranchName] = useState("");
    const [editingName, setEditingName] = useState<string | null>(null);
    const [editingDesc, setEditingDesc] = useState<string | null>(null);
    const [cutNode, setCutNode] = useState<CategoryNodeSummary | null>(null);

    // drag-and-drop move. The payload lives in a ref so that starting a drag does
    // not re-render the dragged row — Safari/WebKit cancels the native drag if the
    // source element re-renders during dragstart. State is only for visual cues.
    const dragNodeRef = useRef<CategoryNodeSummary | null>(null);
    const [dragNode, setDragNode] = useState<CategoryNodeSummary | null>(null);
    const [dragOverId, setDragOverId] = useState<string | null>(null);

    const debounced = useDebouncedValue(query, 200);
    const qTrim = query.trim().toLowerCase();
    const hasExactMatch =
        qTrim.length > 0 && hits.some((h) => h.name.trim().toLowerCase() === qTrim);

    const activeId = activeNode?.id ?? null;
    const activeIsLeaf = !!activeNode && isLeaf(activeNode);

    // Where new branches/categories get attached. A leaf can't hold children, so
    // additions go alongside it (under the leaf's parent).
    const addParentId: string | null = activeNode
        ? activeIsLeaf
            ? activeNode.parentId ?? null
            : activeNode.id
        : null;
    const addParentLabel: string = activeNode
        ? activeIsLeaf
            ? "its parent"
            : `"${activeNode.name}"`
        : "the root";

    // ----- loading -----
    const loadRoots = useCallback(async () => {
        setLoadingRoots(true);
        setError(null);
        try {
            const roots = await getRoots(token);
            setChildrenByParent((prev) => ({ ...prev, [ROOT]: roots }));
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setLoadingRoots(false);
        }
    }, [token]);

    const loadChildren = useCallback(
        async (id: string): Promise<CategoryNodeSummary[]> => {
            const kids = await getChildren(token, id);
            setChildrenByParent((prev) => ({ ...prev, [id]: kids }));
            return kids;
        },
        [token]
    );

    // Reload every level we've already loaded (keeps the visible tree fresh).
    const reloadLoaded = useCallback(async () => {
        const keys = Object.keys(childrenByParent);
        try {
            const pairs = await Promise.all(
                keys.map(async (key) => {
                    const kids = key === ROOT ? await getRoots(token) : await getChildren(token, key);
                    return [key, kids] as const;
                })
            );
            setChildrenByParent((prev) => {
                const next = { ...prev };
                pairs.forEach(([k, v]) => (next[k] = v));
                return next;
            });
        } catch (err) {
            logApiError(err);
        }
    }, [token, childrenByParent]);

    useEffect(() => {
        loadRoots();
    }, [loadRoots]);

    useEffect(() => {
        if (refreshSignal === undefined) return;
        reloadLoaded();
    }, [refreshSignal]); // intentionally only on signal change

    // ----- expand / collapse -----
    const expandNode = useCallback(
        async (node: CategoryNodeSummary) => {
            if (childrenByParent[node.id] === undefined) {
                await loadChildren(node.id);
            }
            setExpanded((prev) => new Set(prev).add(node.id));
        },
        [childrenByParent, loadChildren]
    );

    const collapseNode = useCallback((id: string) => {
        setExpanded((prev) => {
            const s = new Set(prev);
            s.delete(id);
            return s;
        });
    }, []);

    const toggleNode = useCallback(
        (node: CategoryNodeSummary) => {
            if (expanded.has(node.id)) collapseNode(node.id);
            else expandNode(node);
        },
        [expanded, collapseNode, expandNode]
    );

    // Scroll the revealed node's row into view once it renders.
    const pendingScrollRef = useRef<string | null>(null);
    // Remember what we last revealed so the selectedLeafId effect doesn't loop.
    const lastRevealedRef = useRef<string | null>(null);

    // Load + expand the whole path down to a node, then mark it active.
    const revealNode = useCallback(
        async (node: CategoryNodeSummary) => {
            const path = node.ancestorIds ?? [];
            const updates: Record<string, CategoryNodeSummary[]> = {};
            for (const aid of path) {
                if (childrenByParent[aid] === undefined) {
                    updates[aid] = await getChildren(token, aid);
                }
            }
            if (!isLeaf(node) && childrenByParent[node.id] === undefined) {
                updates[node.id] = await getChildren(token, node.id);
            }
            if (Object.keys(updates).length > 0) {
                setChildrenByParent((prev) => ({ ...prev, ...updates }));
            }
            setExpanded((prev) => {
                const s = new Set(prev);
                path.forEach((id) => s.add(id));
                if (!isLeaf(node)) s.add(node.id);
                return s;
            });
            setActiveNode(node);
            lastRevealedRef.current = node.id;
            pendingScrollRef.current = node.id;
        },
        [token, childrenByParent]
    );

    // When the selected leaf changes from outside (e.g. picked via another
    // control), reveal it in the tree so it is visibly selected.
    useEffect(() => {
        if (!selectedLeafId || selectedLeafId === lastRevealedRef.current) return;
        let cancelled = false;
        (async () => {
            try {
                const node = await getCategoryNodeById(token, selectedLeafId);
                if (cancelled) return;
                await revealNode(
                    isLeaf(node) ? toSummary(node as Category) : (node as CategoryNodeSummary)
                );
            } catch (err) {
                logApiError(err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedLeafId, token, revealNode]);

    // ----- click a node -----
    const onClickNode = useCallback(
        async (node: CategoryNodeSummary) => {
            setError(null);
            setEditingName(null);
            setEditingDesc(null);
            setActiveNode(node);
            if (isLeaf(node)) {
                try {
                    const full = await getCategoryNodeById(token, node.id);
                    onSelectLeaf(full as Category);
                } catch (err) {
                    logApiError(err);
                    setError(getUserMessage(err));
                }
            } else {
                toggleNode(node);
            }
        },
        [token, onSelectLeaf, toggleNode]
    );

    // ----- search -----
    useEffect(() => {
        let cancelled = false;
        async function run() {
            if (skipNextSearchRef.current) {
                skipNextSearchRef.current = false;
                setHits([]);
                return;
            }
            const q = debounced.trim();
            // Fire the type-ahead automatically once enough has been typed.
            if (q.length < 3) {
                setHits([]);
                return;
            }
            setSearching(true);
            try {
                let data = await autocompleteCategories(token, q, 8);
                if (cancelled) return;
                if (data.length === 0 && q.length >= 4) {
                    data = await searchCategories(token, q);
                    if (cancelled) return;
                }
                setHits(data);
            } catch (err) {
                if (cancelled) return;
                logApiError(err);
                setHits([]);
            } finally {
                if (!cancelled) setSearching(false);
            }
        }
        run();
        return () => {
            cancelled = true;
        };
    }, [debounced, token]);

    const onEnterSearch = async () => {
        const q = query.trim();
        if (q.length < 2) return;
        setSearching(true);
        try {
            setHits(await searchCategories(token, q));
        } catch (err) {
            logApiError(err);
            setHits([]);
        } finally {
            setSearching(false);
        }
    };

    const pickHit = async (hit: MetadataCategorySearchDTO) => {
        skipNextSearchRef.current = true;
        setHits([]);
        setQuery(hit.name);
        setError(null);
        try {
            const node = await getCategoryNodeById(token, hit.id);
            if (isLeaf(node)) {
                const leaf = node as Category;
                await revealNode(toSummary(leaf));
                onSelectLeaf(leaf);
            } else {
                await revealNode(node as CategoryNodeSummary);
            }
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        }
    };

    // ----- authoring actions -----
    const afterMutation = useCallback(async () => {
        await reloadLoaded();
        onTreeChanged?.();
    }, [reloadLoaded, onTreeChanged]);

    const handleCreateBranch = async () => {
        const name = newBranchName.trim();
        if (!name) return;
        try {
            await createBranch(token, { name, parentId: addParentId });
            setNewBranchName("");
            // Make sure the parent is expanded so the new branch is visible.
            if (addParentId) {
                await loadChildren(addParentId);
                setExpanded((prev) => new Set(prev).add(addParentId));
            } else {
                await loadRoots();
            }
            onTreeChanged?.();
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        }
    };

    const handleRename = async () => {
        if (!activeNode || editingName === null || activeNode.version == null) return;
        const newName = editingName.trim();
        if (!newName || newName === activeNode.name) {
            setEditingName(null);
            return;
        }
        try {
            const updated = await patchCategoryUpdateName(token, {
                id: activeNode.id,
                version: activeNode.version,
                patchTask: "ChangeName",
                newName,
            });
            setActiveNode({ ...activeNode, name: updated.name, version: updated.version });
            setEditingName(null);
            await afterMutation();
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        }
    };

    const handleEditDescription = async () => {
        if (!activeNode || editingDesc === null || activeNode.version == null) return;
        try {
            const updated = await patchCategoryUpdateDescription(token, {
                id: activeNode.id,
                version: activeNode.version,
                patchTask: "ChangeDescription",
                newDescription: editingDesc,
            });
            setActiveNode({ ...activeNode, description: updated.description, version: updated.version });
            setEditingDesc(null);
            await afterMutation();
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        }
    };

    const handleDelete = async (node: CategoryNodeSummary) => {
        // Advisory: count listings still referencing this category (for a
        // branch: any category in its subtree) so the admin knows what the
        // delete leaves behind. Failure to count never blocks the delete.
        let listingNote = "";
        try {
            let ids = [node.id];
            if (!isLeaf(node)) {
                const subtree = await getSubtree(token, node.id);
                ids = ids.concat(subtree.map((n) => n.id));
            }
            const count = await countInventoriesByCategories(token, ids);
            if (count > 0) {
                listingNote =
                    `\n\n⚠ There ${count === 1 ? "is 1 listing" : `are ${count} listings`} in the database ` +
                    (isLeaf(node) ? "for this category." : "for categories in this subtree.") +
                    " After the delete they will no longer be findable by category search.";
            }
        } catch (err) {
            logApiError(err);
        }
        const ok = window.confirm(
            `Delete "${node.name}"? If it has descendants they will be removed too.${listingNote}`
        );
        if (!ok) return;
        try {
            await deleteCategoryNode(token, node.id, true);
            collapseNode(node.id);
            if (activeNode?.id === node.id) setActiveNode(null);
            await afterMutation();
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        }
    };

    const placeCutNodeHere = async () => {
        if (!cutNode || cutNode.version == null) return;
        try {
            await moveCategory(token, {
                id: cutNode.id,
                version: cutNode.version,
                patchTask: "MoveCategory",
                newParentId: addParentId,
            });
            setCutNode(null);
            await afterMutation();
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        }
    };

    // Cannot drop a node into itself, into its own subtree, or under a leaf.
    const canPlaceHere =
        !!cutNode &&
        !activeIsLeaf &&
        cutNode.id !== activeNode?.id &&
        !(activeNode?.ancestorIds ?? []).includes(cutNode.id);

    // ----- drag-and-drop move -----
    // A drop target is valid when it is a branch (or the root zone, target=null),
    // is not the dragged node, and is not inside the dragged node's own subtree.
    const isValidDrop = (target: CategoryNodeSummary | null): boolean => {
        const dn = dragNodeRef.current;
        if (!dn) return false;
        if (!target) return dn.parentId != null; // moving to root only if not already a root
        return (
            !isLeaf(target) &&
            target.id !== dn.id &&
            target.id !== dn.parentId && // already there
            !(target.ancestorIds ?? []).includes(dn.id)
        );
    };

    const endDrag = () => {
        dragNodeRef.current = null;
        setDragNode(null);
        setDragOverId(null);
    };

    const performMove = async (newParentId: string | null) => {
        const moved = dragNodeRef.current;
        const version = moved?.version;
        if (!moved || version == null) return;
        endDrag();
        try {
            await moveCategory(token, {
                id: moved.id,
                version,
                patchTask: "MoveCategory",
                newParentId,
            });
            if (newParentId) {
                await loadChildren(newParentId);
                setExpanded((prev) => new Set(prev).add(newParentId));
            } else {
                await loadRoots();
            }
            await afterMutation();
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        }
    };

    // ----- render the tree -----
    const renderNode = (node: CategoryNodeSummary, depth: number): React.ReactNode => {
        const branch = !isLeaf(node);
        const open = expanded.has(node.id);
        const kids = childrenByParent[node.id];
        const isActive = node.id === activeId;
        const isSelected = node.id === selectedLeafId;
        const isDropTarget = authoring && dragOverId === node.id && isValidDrop(node);
        return (
            <div key={node.id}>
                <div
                    ref={(el) => {
                        if (el && pendingScrollRef.current === node.id) {
                            pendingScrollRef.current = null;
                            el.scrollIntoView({ block: "nearest" });
                        }
                    }}
                    style={rowStyle(depth, isActive, isSelected, isDropTarget)}
                    onClick={() => onClickNode(node)}
                    draggable={authoring}
                    onDragStart={(e) => {
                        if (!authoring) return;
                        e.stopPropagation();
                        // Set the payload synchronously (ref, no re-render) so Safari
                        // does not cancel the drag; reflect it in state on the next tick.
                        dragNodeRef.current = node;
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", node.id);
                        setTimeout(() => setDragNode(node), 0);
                    }}
                    onDragEnd={endDrag}
                    onDragOver={(e) => {
                        if (!authoring || !dragNodeRef.current) return;
                        if (isValidDrop(node)) {
                            e.preventDefault();
                            e.stopPropagation();
                            e.dataTransfer.dropEffect = "move";
                            if (dragOverId !== node.id) setDragOverId(node.id);
                        }
                    }}
                    onDrop={(e) => {
                        if (!authoring || !dragNodeRef.current) return;
                        if (isValidDrop(node)) {
                            e.preventDefault();
                            e.stopPropagation();
                            performMove(node.id);
                        }
                    }}
                    title={authoring ? "Drag to move" : undefined}
                >
                    {branch ? (
                        <button
                            className="tree-toggle"
                            style={toggleStyle}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleNode(node);
                            }}
                            aria-label={open ? "Collapse" : "Expand"}
                        >
                            {open ? "▼" : "▶"}
                        </button>
                    ) : (
                        <span style={toggleStyle} />
                    )}
                    <span style={{ marginRight: "0.4rem" }}>{branch ? "📁" : "🏷️"}</span>
                    <span style={{ fontWeight: isActive || isSelected ? 700 : 500 }}>{node.name}</span>
                    {isSelected && (
                        <span style={{ marginLeft: "0.4rem", color: "var(--accent, #2e7d32)", fontWeight: 700 }}>
                            ✓ selected
                        </span>
                    )}
                    {node.description && (
                        <span className="item-description" style={{ marginLeft: "0.5rem" }}>
                            {node.description}
                        </span>
                    )}
                </div>

                {branch && open && (
                    <div>
                        {kids === undefined && (
                            <div className="item-description" style={{ paddingLeft: indent(depth + 1) }}>
                                Loading…
                            </div>
                        )}
                        {kids && kids.length === 0 && (
                            <div className="item-description" style={{ paddingLeft: indent(depth + 1) }}>
                                (empty)
                            </div>
                        )}
                        {kids && kids.map((c) => renderNode(c, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    const roots = childrenByParent[ROOT];

    return (
        <div className="search-container search-container--narrow">
            <h3 style={{ marginBottom: "0.75rem" }}>
                {authoring ? "Category Tree" : "Search Category"}
            </h3>

            {/* Search box */}
            <div className="search-input-wrapper">
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && onEnterSearch()}
                    placeholder="Type 2+ letters to find & reveal in the tree…"
                />
                {hits.length > 0 && (
                    <div className="search-dropdown">
                        {hits.map((h) => (
                            <div
                                key={h.id}
                                onClick={() => pickHit(h)}
                                className="search-dropdown-item"
                            >
                                <div className="item-name">{h.name}</div>
                                {h.description && (
                                    <div className="item-description">{h.description}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {searching && <div className="status-loading" style={{ marginTop: "0.5rem" }}>Searching…</div>}

            {/* "create new" affordance for navigation panels */}
            {!authoring && onCreateNew && qTrim.length >= 2 && !hasExactMatch && (
                requestSavedFor === qTrim ? (
                    <div className="success-message" style={{ marginTop: "0.5rem" }}>
                        Request saved for "{qTrim}"
                    </div>
                ) : (
                    <button
                        onClick={() => onCreateNew(qTrim)}
                        className="btn-outline"
                        style={{ marginTop: "0.5rem" }}
                    >
                        {createNewLabel} "{qTrim}"
                    </button>
                )
            )}

            {error && <div className="status-error" style={{ marginTop: "0.5rem" }}>{error}</div>}

            {authoring && (
                <div className="item-description" style={{ marginTop: "0.5rem" }}>
                    Tip: drag a branch or category onto a branch to move it.
                </div>
            )}

            {/* Tree */}
            <div style={treeBoxStyle}>
                {authoring && dragNode && (
                    <div
                        style={rootDropStyle(dragOverId === ROOT && isValidDrop(null))}
                        onDragOver={(e) => {
                            if (!dragNodeRef.current || !isValidDrop(null)) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            setDragOverId(ROOT);
                        }}
                        onDrop={(e) => {
                            if (!dragNodeRef.current || !isValidDrop(null)) return;
                            e.preventDefault();
                            performMove(null);
                        }}
                    >
                        ⤴ Drop here to move "{dragNode.name}" to the top level (root)
                    </div>
                )}
                {loadingRoots && roots === undefined ? (
                    <div className="status-loading">Loading tree…</div>
                ) : roots && roots.length > 0 ? (
                    roots.map((r) => renderNode(r, 0))
                ) : (
                    <div className="item-description">No categories yet.</div>
                )}
            </div>

            {/* Authoring controls */}
            {authoring && (
                <div style={authoringBoxStyle}>
                    <div style={{ fontWeight: 600, marginBottom: "0.35rem" }}>
                        Edit tree
                        {activeNode && (
                            <span className="item-description" style={{ marginLeft: "0.5rem" }}>
                                selected: {activeNode.name} ({activeIsLeaf ? "category" : "branch"})
                            </span>
                        )}
                    </div>

                    {/* Inline rename / description editors for the active branch */}
                    {activeNode && !activeIsLeaf && editingName !== null && (
                        <div style={{ display: "flex", gap: "0.5rem", margin: "0.25rem 0" }}>
                            <input value={editingName} onChange={(e) => setEditingName(e.target.value)} autoFocus />
                            <button className="btn-sm btn-success" onClick={handleRename}>Save</button>
                            <button className="btn-sm btn-secondary" onClick={() => setEditingName(null)}>Cancel</button>
                        </div>
                    )}
                    {activeNode && !activeIsLeaf && editingDesc !== null && (
                        <div style={{ display: "flex", gap: "0.5rem", margin: "0.25rem 0" }}>
                            <input
                                value={editingDesc}
                                onChange={(e) => setEditingDesc(e.target.value)}
                                placeholder="Description"
                                style={{ flex: 1 }}
                            />
                            <button className="btn-sm btn-success" onClick={handleEditDescription}>Save</button>
                            <button className="btn-sm btn-secondary" onClick={() => setEditingDesc(null)}>Cancel</button>
                        </div>
                    )}

                    {/* Add a branch / category */}
                    <div className="item-description" style={{ marginBottom: "0.35rem" }}>
                        New nodes are added under {addParentLabel}
                        {activeIsLeaf && " (a category can't contain children)"}.
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                        <input
                            value={newBranchName}
                            onChange={(e) => setNewBranchName(e.target.value)}
                            placeholder="New branch name"
                            onKeyDown={(e) => e.key === "Enter" && handleCreateBranch()}
                        />
                        <button className="btn-sm btn-success" onClick={handleCreateBranch}>
                            + Branch
                        </button>
                        {onCreateLeafHere && (
                            <button
                                className="btn-sm btn-secondary"
                                onClick={() => onCreateLeafHere(addParentId, "")}
                            >
                                + Category
                            </button>
                        )}
                    </div>

                    {/* Actions on the active node */}
                    {activeNode && (
                        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
                            {!activeIsLeaf && (
                                <>
                                    <button className="btn-sm btn-secondary" onClick={() => setEditingName(activeNode.name)}>
                                        Rename
                                    </button>
                                    <button
                                        className="btn-sm btn-secondary"
                                        onClick={() => setEditingDesc(activeNode.description ?? "")}
                                    >
                                        Edit description
                                    </button>
                                </>
                            )}
                            <button className="btn-sm btn-secondary" onClick={() => setCutNode(activeNode)}>
                                Move "{activeNode.name}"
                            </button>
                            <button className="btn-sm btn-danger" onClick={() => handleDelete(activeNode)}>
                                Delete "{activeNode.name}"
                            </button>
                        </div>
                    )}

                    {/* Move (cut/paste) bar */}
                    {cutNode && (
                        <div style={moveBarStyle}>
                            <span>
                                Moving <strong>{cutNode.name}</strong>. Select a destination branch in
                                the tree (or none for root), then place it.
                            </span>
                            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                                <button className="btn-sm btn-success" disabled={!canPlaceHere} onClick={placeCutNodeHere}>
                                    Place under {addParentLabel}
                                </button>
                                <button className="btn-sm btn-secondary" onClick={() => setCutNode(null)}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ----- styles -----
const indent = (depth: number) => `${depth * 1.25 + 0.25}rem`;

const rowStyle = (
    depth: number,
    active: boolean,
    selected: boolean,
    dropTarget = false
): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    padding: "0.3rem 0.4rem",
    paddingLeft: indent(depth),
    cursor: "pointer",
    userSelect: "none",
    borderRadius: "var(--radius-sm, 4px)",
    background: dropTarget
        ? "var(--accent-soft, #e6f4ea)"
        : selected
        ? "var(--accent-soft, #e6f4ea)"
        : active
        ? "var(--bg-secondary)"
        : undefined,
    boxShadow: selected ? "inset 3px 0 0 var(--accent, #2e7d32)" : undefined,
    outline: dropTarget ? "2px dashed var(--accent, #2e7d32)" : undefined,
});
const toggleStyle: React.CSSProperties = {
    width: "1.25rem",
    minWidth: "1.25rem",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    fontSize: "0.75rem",
    color: "var(--text-secondary)",
    textAlign: "left",
};
const rootDropStyle = (active: boolean): React.CSSProperties => ({
    padding: "0.4rem 0.5rem",
    marginBottom: "0.4rem",
    border: "2px dashed var(--accent, #2e7d32)",
    borderRadius: "var(--radius-sm, 4px)",
    fontSize: "0.85rem",
    color: "var(--text-secondary)",
    background: active ? "var(--accent-soft, #e6f4ea)" : "transparent",
    textAlign: "center",
});
const treeBoxStyle: React.CSSProperties = {
    marginTop: "0.75rem",
    border: "1px solid var(--border-color)",
    borderRadius: "var(--radius-md, 6px)",
    padding: "0.5rem",
    maxHeight: "22rem",
    overflowY: "auto",
    background: "var(--bg-primary)",
};
const authoringBoxStyle: React.CSSProperties = {
    marginTop: "0.75rem",
    paddingTop: "0.75rem",
    borderTop: "1px dashed var(--border-color)",
};
const moveBarStyle: React.CSSProperties = {
    marginTop: "0.75rem",
    padding: "0.75rem",
    border: "1px solid var(--border-color)",
    borderRadius: "var(--radius-md, 6px)",
    background: "var(--bg-primary)",
};

export default CategoryTreeNavigator;
